import React from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { InboxConversation, InboxCounts } from 'data/api/conversation.api';
import { SearchTracking } from 'components/SearchTracking';
import { SnoozeFailure } from 'components/conversation/SnoozeButton';
import { QueueRow } from './QueueRow';

export type QueueTab = 'open' | 'snoozed' | 'closed';

interface IInboxQueueProps {
  conversations: InboxConversation[];
  counts?: InboxCounts | null;
  closedCount?: number;
  tab: QueueTab;
  onTabChange: (tab: QueueTab) => void;
  selectedMemberId?: string;
  updatingIds: Set<string>;
  loading: boolean;
  loadError: boolean;
  searchResetKey: number;
  hasMore: boolean;
  loadingMore: boolean;
  onSearch: (value: string) => void;
  onSelect: (conversation: InboxConversation) => void;
  onToggleStatus: (conversation: InboxConversation) => void;
  onSnooze: (
    conversation: InboxConversation,
    until: string,
  ) => Promise<SnoozeFailure | null>;
  onLoadMore: () => void;
  onRetry: () => void;
}

export const InboxQueue: React.FC<IInboxQueueProps> = ({
  conversations,
  counts,
  closedCount,
  tab,
  onTabChange,
  selectedMemberId,
  updatingIds,
  loading,
  loadError,
  searchResetKey,
  hasMore,
  loadingMore,
  onSearch,
  onSelect,
  onToggleStatus,
  onSnooze,
  onLoadMore,
  onRetry,
}) => {
  const tabs: { value: QueueTab; label: string; count?: number }[] = [
    { value: 'open', label: 'Open', count: counts?.open },
    { value: 'snoozed', label: 'Snoozed', count: counts?.snoozed },
    { value: 'closed', label: 'Closed', count: closedCount },
  ];

  return (
    <Stack
      sx={{
        width: '344px',
        minWidth: '344px',
        // Clip the column so a child that asks for more width than the column
        // has can never paint over the thread panel beside it.
        overflow: 'hidden',
        borderRight: '1px solid #EFEDEB',
        backgroundColor: '#FFFFFF',
        height: '100%',
      }}
    >
      <Stack sx={{ padding: '16px 14px 0', gap: '12px' }}>
        <Typography
          sx={{ fontSize: '20px', fontWeight: 700, color: '#213B54' }}
        >
          Conversations
        </Typography>
        <SearchTracking
          key={searchResetKey}
          label="Search for a member"
          width="100%"
          onChange={onSearch}
          debounce={800}
        />
        <Stack
          direction="row"
          gap={2}
          sx={{ borderBottom: '1px solid #EFEDEB' }}
        >
          {tabs.map((item) => {
            const active = tab === item.value;
            const label =
              item.count === undefined
                ? item.label
                : `${item.label} · ${item.count}`;
            return (
              <Button
                key={item.value}
                disableRipple
                onClick={() => onTabChange(item.value)}
                sx={{
                  minWidth: 'unset',
                  padding: '6px 2px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: active ? '#213B54' : '#7D818C',
                  borderBottom: active
                    ? '2px solid #213B54'
                    : '2px solid transparent',
                  borderRadius: 0,
                  ':hover': { backgroundColor: 'transparent' },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Stack>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#9A9A9A',
          }}
        >
          MOST RECENT FIRST
        </Typography>
      </Stack>

      <Stack
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: '4px' }}
      >
        {loading && !conversations.length && (
          <Stack alignItems="center" paddingY="48px">
            <CircularProgress size={26} sx={{ color: '#356766' }} />
          </Stack>
        )}

        {!loading && !conversations.length && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            paddingY="48px"
          >
            <ForumOutlinedIcon sx={{ color: '#7D818C', fontSize: '32px' }} />
            <Typography
              sx={{ color: '#213B54', fontWeight: 600, fontSize: '15px' }}
            >
              {loadError ? 'Could not load the inbox' : 'No conversations here'}
            </Typography>
            {loadError && (
              <Button
                onClick={onRetry}
                sx={{
                  color: '#356766',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Try again
              </Button>
            )}
          </Stack>
        )}

        {conversations.map((conversation) => (
          <QueueRow
            key={conversation.memberUserId}
            conversation={conversation}
            selected={conversation.memberUserId === selectedMemberId}
            busy={updatingIds.has(conversation.memberUserId)}
            onSelect={onSelect}
            onToggleStatus={onToggleStatus}
            onSnooze={onSnooze}
          />
        ))}

        {hasMore && (
          <Button
            onClick={onLoadMore}
            disabled={loadingMore}
            sx={{
              margin: '12px auto',
              color: '#356766',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
